import { IDisposable } from "Global/Base/IDisposable";
import { PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { EventsController } from "../../../../Global/Dom/EventsController";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { Checkbox } from "../../../UI.BaseControls/Checkbox";
import { GenericControl } from "../../../UI.BaseControls/GenericControl";
import { createElement, registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { CollapseState } from "./TreeViewDataRow";

/**
 * Třída pro zobrazení stromové struktury Tříd a atributů
 *
 * @event change Při zašktrnutí řádku
 * @event childDataRequest žádost pro dočtení potomku při otevření stromu
 * @event changeByUser Událost vyvolaná při zaškrtnutí tlačítka uživatelskou akci
 */
export class TreeClassViewRow extends HTMLElement implements IDisposable {
    private _checkboxElement: Checkbox;
    private _state: CollapseState;
    private _eventController: EventsController = new EventsController();

    protected _childrenElement: HTMLElement;
    private _expandElement: HTMLElement;
    protected _dataElement: HTMLElement;
    protected _stateChangeByUser: boolean = false;
    protected _openStateChangeByUser: boolean = false;
    private _loaderElement: HTMLElement;
    private _hasLoaderElement: boolean = true;
    private _nameContainerElement: HTMLElement;
    private _values: string[] = [];

    private _expandDataLoaded = false;
    private _isLastNode = false;
    private _isSelected: boolean = false;
    private _condition: string = "";
    private _isConditionValid: Promise<boolean> = Promise.resolve(true);

    private _isDisposed = false;

    //private _isSelected: boolean;
    private _isDataLoadedDefferred = PromiseUtils.deferStateful<void>();

    public dataKey: string;

    /**
     * Informace o tom, zda byl stav změněn ručně klikem uživatele.
     */
    public get stateChangeByUser(): boolean {
        return this._stateChangeByUser;
    }

    public set stateChangeByUser(val: boolean) {
        this._stateChangeByUser = val;
    }

    /**
     * Informace o tom, zda byl přepnut mod otevření uživatelem
     */
    public get openStateChangeByUser(): boolean {
        return this._openStateChangeByUser;
    }

    public get isConditionValid(): Promise<boolean> {
        return this._isConditionValid;
    }

    public set isConditionValid(v: Promise<boolean>) {
        this._isConditionValid = v;
    }

    public get condition(): string {
        return this._condition;
    }
    public set condition(values) {
        this._condition = values;
    }

    public get values(): string[] {
        return this._values;
    }
    public set values(values) {
        this._values = values;
    }

    public get state(): CollapseState {
        return this._state;
    }

    public set state(v: CollapseState) {
        this.toggleExpand(v);
    }

    public set isRequired(v: boolean) {
        this.classList.toggle("is-required", v);
    }

    public get isRequired(): boolean {
        return this.classList.contains("is-required");
    }

    public get isChecked(): boolean {
        return this._checkboxElement.checked;
    }

    public set isChecked(v: boolean) {
        this._checkboxElement.checked = v;
    }

    public set isSelected(v: boolean) {
        if (this._isSelected == v)
            return;

        this._isSelected = v;
        this.classList.toggle("selected", v);
        if (v)
            this.callEvent("selectedRecordChanged");
    }

    public get isSelected(): boolean {
        return this._isSelected;
    }

    public set isLastNode(v: boolean) {
        this._isLastNode = v;
        this.classList.toggle("last-node", v);
        this._removeLoaderElement();
    }

    public get isLastNode(): boolean {
        return this._isLastNode;
    }

    public get isDataLoaded(): boolean {
        return this._isDataLoadedDefferred.isSettled;
    }

    public get childRows(): TreeClassViewRow[] {
        if (!this._childrenElement || this._hasLoaderElement)
            return [];
        return Array.from(this._childrenElement.children) as TreeClassViewRow[];
    }

    public get parentRow(): TreeClassViewRow | null {
        if (this.parentElement && this.parentElement.parentElement instanceof TreeClassViewRow) {
            return this.parentElement.parentElement;
        }

        return null;
    }

    public get dataLoadPromise(): Promise<void> {
        return this._isDataLoadedDefferred.promise;
    }

    protected static get observedAttributes(): readonly string[] {
        return ["state"];
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        this.classList.add("tree-view-datarow", "tree-class-view-row");

        this.setAttribute("state", "collapsed");

        this._nameContainerElement = createElement({ classes: ["name-container"] });
        this._expandElement = createElement({
            type: "div",
            classes: ["btn", "flat", "expander", "icon", "ui-triangle-right"],
        });
        this._dataElement = createElement({ classes: ["data"] });
        this._nameContainerElement.appendChild(this._expandElement);
        this._nameContainerElement.appendChild(this._dataElement);

        this._childrenElement = createElement({ type: "ul", classes: ["children"] });
        this._loaderElement = createElement({ classes: ["loader", "loading"] });
        Localization.getTextPromise("GUI-580532").then((v) => this._loaderElement.textContent = v); // Načítám

        this.appendChild(this._nameContainerElement);
        this.appendChild(this._childrenElement);
        this._childrenElement.appendChild(this._loaderElement);

        const expandListener = () => {
            this.toggleExpand();
            this._openStateChangeByUser = true;
        };

        this._dataElement.addEventListener("click", async () => this._clickDataElement());

        this._dataElement.addEventListener("dblclick", (e) => {
            if (this._checkboxElement.contains(e.target as HTMLElement)) return;
            expandListener();
        });
        this._expandElement.addEventListener("click", expandListener);

        this.dataLoadPromise.then(() => {
            this._removeLoaderElement();
        });
    }

    protected connectedCallback(): void {
        this._eventController.addListener(this, "changeByUser", (e) => {
            const row = e.target as TreeClassViewRow;

            if (!row.isLastNode) {
                if (row.isDataLoaded) {
                    // Pokud ma trida potomky, dojde k zaskrtnuti vsech potomku na prvni urovni
                    if (row.isChecked) row.childRows.forEach(childRow => childRow.toggleChecked(row.isChecked));
                    // Pokud odksrtneme nadrazenou tridu dojde k odskrtnuti vsech potomku na vsech podrizenych tridach
                    else this._goFullTree(row, node => node.toggleChecked(false), "down");
                }
            }

            if (row.parentRow) {
                // pokud zaskrtneme potomka je treba zaskrtnout i materskou tridu
                if (row.isChecked) {
                    this._goFullTree(row.parentRow, node => node.toggleChecked(true), "up");
                } else {
                // pokud se potomek odskrtnul zkontroluje se, jestli je neco zaskrtnuto a pokud ne, materska trida se odznaci
                    this._goFullTree(row.parentRow, node => {
                        let checked = false;
                        for (const childRow of node.childRows) {
                            if (childRow.isChecked) {
                                checked = true;
                                break;
                            }
                        }
                        if (!checked) node.toggleChecked(false);
                    }, "up");
                }
            }
        });

        this._eventController.addListener(this, "childDataRequest", async (e) => {
            const row = e.target as TreeClassViewRow;

            if (!row.stateChangeByUser) return;
            await row.dataLoadPromise;
            if (row.isChecked) row.childRows.forEach(childRow => childRow.toggleChecked(row.isChecked));
        });
    }

    /**
     * Projde strom od počátečního node, pouze načtené větve
     * @param start Startovní node
     * @param action Akce, která se má provést
     * @param direction Směr rekurze
     * @private
     */
    private _goFullTree(start: TreeClassViewRow, action: (node: TreeClassViewRow) => void, direction: "up" | "down" = "down"): void {
        action(start);
        if (direction === "down") {
            if (start.isLastNode || !start.isDataLoaded) return;
            else {
                for (const node of start.childRows) {
                    this._goFullTree(node, action, direction);
                }
            }
        } else {
            if (start.parentRow != null) this._goFullTree(start.parentRow, action, direction);
        }
    }

    protected disconnectedCallback(): void {
        this._eventController.removeAllListeners();
    }

    private _clickDataElement(): void {
        this.isSelected = true;
    }

    private _removeLoaderElement(): void {
        this._hasLoaderElement = false;
        this._loaderElement?.remove();
    }

    private _checkboxChange(): void {
        //if (this._skipCheckEvent) return;
        /*if (!this.isLastNode && this.isDataLoaded) {
            for (const child of this.childRows) {
                child.isChecked = this._checkboxElement.controlValue;
            }
        }*/

        /*if (this.parentRow) {
            if (this.isChecked && !this.parentRow.isChecked) {
                this.parentRow.toggleChecked(true, false);
            }
        }*/
        this.callEvent("change", {}, true);
    }

    public toggleChecked(force?: boolean): void {
        this.isChecked = force ?? !this.isChecked;
    }

    public toggleExpand(force?: CollapseState): void {
        if (force == null) {
            this._state = this._state === CollapseState.collapsed ? CollapseState.expanded : CollapseState.collapsed;
        } else {
            if (force === this._state)
                return;
            this._state = force;
        }

        if (!this._expandDataLoaded && this._state === CollapseState.expanded) {
            if (!this.isDataLoaded && !this.isLastNode) {
                this.callEvent("childDataRequest", { promiseResolvedFnc: this._isDataLoadedDefferred.resolve }, true);
            }

            this._expandDataLoaded = true;
        }

        this.setAttribute("state", CollapseState[this._state]);
    }

    public render(): HTMLElement {
        this._dataElement.appendChild(this._createFirstCell());

        for (const val of this._values) {
            this._dataElement.appendChild(createElement({ type: "td", classes: ["cellContainer"], html: `<span>${val}</span>` }));
        }

        return this as HTMLElement;
    }

    protected attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        GenericControl.attributeChangedCallback(this as HTMLElement, attrName, oldVal, newVal);

        if (attrName === "state") {
            this.state = CollapseState[newVal as "expanded" | "collapsed"] || CollapseState.collapsed;
        }
    }

    public override appendChild<T extends Node>(newChild: T): T {
        if (newChild instanceof TreeClassViewRow) {
            //if (this.isChecked) newChild.isChecked = this.isChecked;
            return this._childrenElement.appendChild(newChild);
        }
        return super.appendChild(newChild);
    }

    public override insertAdjacentElement(position: "beforebegin" | "afterbegin" | "beforeend" | "afterend", insertedElement: Element): Element {
        if (insertedElement instanceof TreeClassViewRow && (position == "afterbegin" || position == "beforeend")) {
            return this._childrenElement.insertAdjacentElement(position, insertedElement);
        }

        return super.insertAdjacentElement(position, insertedElement);
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public callEvent(eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        return GenericControl.callEvent(this as HTMLElement, eventName, detail, bubbles, cancelable);
    }

    protected _createFirstCell(): HTMLElement {
        const checkboxContainer = createElement({ type: "div", classes: "record-control-cell CheckBox" });

        this._checkboxElement = new Checkbox();
        this._checkboxElement.standardAction = true;
        this._checkboxElement.isNullable = false;
        this._checkboxElement.addEventListener("change", () => this._checkboxChange());
        this._checkboxElement.addEventListener("click", () => {
            this._stateChangeByUser = true;
            this.callEvent("changeByUser", {}, true);
        });

        this._checkboxElement.setTabIndexOnInnerElement(-1);

        checkboxContainer.appendChild(this._checkboxElement as HTMLElement);

        return checkboxContainer;
    }

    public dispose(): void {
        if (this._isDisposed) {
            return;
        }

        this._eventController.removeAllListeners();

        for (const row of this.childRows) {
            row.dispose();
        }
        this._isDisposed = true;
    }
}

registerCustomElement("tsc-TreeClassViewRow", TreeClassViewRow);
